<script lang="ts" setup>
import {AuthSession} from '@supabase/supabase-js';
import {computed, onMounted, ref, toRefs, watch} from 'vue';
import Icon from '/src/components/layout/Icon.vue';
import {
    getFullDaysLeft,
    getNavbarAccessLabel,
    isLifetimeAccess,
    isOneTimePass,
    isStandaloneOneTimePass,
} from '/src/components/layout/NavbarAuthSection';
import {useAppSession} from '/src/core/app-session';

const props = defineProps<{
    auth?: AuthSession;
    plan: string | undefined;
    isSubscriptionActive: boolean;
    userAvatarText: string | undefined;
    productName?: string | null;
    subscriptionId?: string | null;
    subscriptionEndsAt?: string | null;
}>();

const emit = defineEmits<{
    (e: 'open-manage-subscription-modal'): void;
    (e: 'open-cancellation-modal'): void;
    (e: 'logout'): void;
    (e: 'sign-in-click'): void;
}>();

const session = useAppSession();
const {subscription} = toRefs(session.state);

const isSurveyCompleted = ref(false);
let target: EventTarget | null = null;

function readSurveyCompleted() {
    const userId = subscription.value?.user_id ?? props.auth?.user?.id;
    if (userId) {
        isSurveyCompleted.value = !!localStorage.getItem(`lopaka_survey_completed_${userId}`);
    }
}

onMounted(() => {
    readSurveyCompleted();
    if (props.auth?.user?.id) {
        void session.refreshSubscription(props.auth.user.id);
    }
});

watch(() => subscription.value?.user_id, readSurveyCompleted);

function checkSubscription(e: MouseEvent) {
    if (!target) {
        target = e.target;
        void session.refreshSubscription(props.auth.user.id);
    }
}

function checkSubscriptionOut() {
    target = null;
}

const hasOneTimePass = computed(() => isOneTimePass(props.productName));
const hasStandaloneOneTimePass = computed(() => isStandaloneOneTimePass(props.productName, props.subscriptionId));
const hasLifetimeAccess = computed(() => isLifetimeAccess(props.productName));
const accessLabel = computed(() => getNavbarAccessLabel(props.productName));

const daysLeft = computed(() => getFullDaysLeft(props.subscriptionEndsAt));

function blurActive() {
    (document.activeElement as HTMLElement)?.blur();
}
</script>

<template>
    <div
        v-if="auth"
        data-testid="account-menu"
        class="dropdown dropdown-end dropdown-hover"
        @mouseleave="blurActive"
        @mouseover="checkSubscription($event)">
        <div
            tabindex="0"
            role="button"
            class="btn btn-circle avatar">
            <div
                v-if="auth.user.user_metadata.avatar_url"
                class="w-8 rounded-full">
                <img
                    :src="auth.user.user_metadata.avatar_url"
                    :alt="auth.user.user_metadata.user_name"
                    :title="auth.user.user_metadata.full_name" />
            </div>
            <div
                v-else
                class="avatar placeholder">
                <div
                    class="bg-neutral text-neutral-content flex h-8 w-8 items-center justify-center rounded-full text-center">
                    <span class="text leading-none uppercase">{{ userAvatarText }}</span>
                </div>
            </div>
        </div>
        <ul
            tabindex="0"
            class="menu menu-sm bg-neutral dropdown-content top-0 right-0 z-1 w-48 rounded-lg"
            @click="($event.currentTarget as HTMLElement).blur()"
            @mouseleave="checkSubscriptionOut()">
            <li class="menu-title flex flex-row items-center justify-between gap-2 p-1 pl-2">
                <div
                    data-testid="account-email"
                    class="max-w-32 truncate">
                    {{ auth.user.email }}
                </div>
                <template v-if="isSubscriptionActive && plan === 'PLUS'">
                    <div
                        class="tooltip tooltip-bottom font-normal"
                        data-tip="Plus">
                        <Icon
                            type="lopaka_plus"
                            sm />
                    </div>
                </template>
                <template v-else-if="isSubscriptionActive && plan === 'HOBBY'">
                    <div
                        class="tooltip tooltip-bottom font-normal"
                        data-tip="Hobby">
                        <Icon
                            type="lopaka_hobby"
                            sm />
                    </div>
                </template>
            </li>
            <li>
                <router-link
                    data-testid="account-link-projects"
                    to="/projects"
                    >Projects</router-link
                >
            </li>
            <li>
                <router-link
                    data-testid="account-link-assets"
                    to="/assets"
                    >Assets</router-link
                >
            </li>
            <li>
                <router-link
                    data-testid="account-link-gallery"
                    to="/gallery">
                    Gallery
                </router-link>
            </li>
            <li>
                <a
                    data-testid="account-link-docs"
                    href="/pages/docs/"
                    target="_blank">
                    Docs
                </a>
            </li>
            <div class="border-base-300 my-0 mb-1 h-1 border-b"></div>
            <li
                v-if="isSubscriptionActive && hasLifetimeAccess"
                class="pointer-events-none">
                <span class="text-base-content/60">Lifetime access</span>
            </li>
            <li
                v-if="
                    isSubscriptionActive && daysLeft !== null && (subscription?.status !== 'active' || hasOneTimePass)
                "
                class="pointer-events-none">
                <span class="text-base-content/60">
                    {{ accessLabel }}: {{ daysLeft }} day{{ daysLeft === 1 ? '' : 's' }}
                    left
                </span>
            </li>
            <li
                v-if="
                    isSubscriptionActive &&
                    !hasLifetimeAccess &&
                    (isSurveyCompleted || subscription?.status === 'cancelled' || subscription?.status === 'canceled')
                ">
                <a @click="emit('open-manage-subscription-modal')"> Manage Subscription </a>
            </li>
            <li
                v-else-if="
                    isSubscriptionActive && !isSurveyCompleted && !hasStandaloneOneTimePass && !hasLifetimeAccess
                ">
                <a
                    class="text-error font-semibold"
                    @click="emit('open-cancellation-modal')">
                    Cancel Subscription
                </a>
            </li>
            <li v-if="!isSubscriptionActive || plan !== 'PLUS'">
                <router-link
                    data-testid="account-upgrade"
                    to="/price#upgrade"
                    class="text-success">
                    {{ auth ? 'Upgrade' : 'Price' }}
                </router-link>
            </li>
            <li>
                <a
                    data-testid="account-contact"
                    :href="`https://lopaka.fibery.io/@public/forms/cot3nHiE?email=${auth?.user?.email ?? ''}`"
                    target="_blank">
                    Contact Us
                </a>
            </li>
            <div class="border-base-300 my-0 mb-1 h-1 border-b"></div>

            <li>
                <a
                    href="#"
                    data-testid="account-sign-out"
                    @click="emit('logout')">
                    Sign Out
                </a>
            </li>
        </ul>
    </div>

    <div v-else>
        <router-link
            data-testid="navbar-sign-in"
            class="btn btn-outline btn-sm btn-success ml-2 uppercase"
            to="/login"
            @click="emit('sign-in-click')">
            Sign In
        </router-link>
    </div>
</template>
